'use client'

import Image from 'next/image'
import { Annotation, Annotation2 } from '@/types/annotation'

// --------------

interface ProductResult {
  name: string
  price: string
  source: string
  url: string
  imageUrl?: string
  rating?: number
  reviews?: number
}

// --------------

interface PriceComparisonResult {
  products: ProductResult[]
  query: string
}

// --------------

// Render star rating as filled/half/empty stars
function StarRating({ rating }: { rating: number }) {
  const full = Math.floor(rating)
  const half = rating - full >= 0.5
  const stars = []

  for (let i = 0; i < 5; i++) {
    if (i < full) {
      stars.push(<span key={i} className="text-amber-500">★</span>)
    } else if (i === full && half) {
      stars.push(
        <span key={i} className="text-muted-foreground/40 relative">
          ★
          <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
            <span className="text-amber-500">★</span>
          </span>
        </span>
      )
    } else {
      stars.push(<span key={i} className="text-muted-foreground/40">★</span>)
    }
  }

  return (
    <span className="flex text-[11px]" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {stars}
    </span>
  )
}

// --------------

function ProductCard({ product }: { product: ProductResult }) {
  return (
    <a
      href={product.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`View ${product.name} at ${product.source}`}
      className="border-border/40 bg-card/50 hover:border-primary/30 group flex w-56 shrink-0 flex-col overflow-hidden rounded-xl border backdrop-blur-sm transition-all hover:shadow-md"
    >
      {/* Product image */}
      {product.imageUrl && (
        <div className="relative h-24 w-full overflow-hidden bg-white/5">
          <Image
            src={product.imageUrl}
            alt={product.name}
            fill
            className="object-contain p-2"
            sizes="256px"
            unoptimized
          />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col gap-1 p-3">
        {/* Product name */}
        <p className="text-foreground line-clamp-2 text-sm font-medium leading-tight">
          {product.name}
        </p>

        {/* Price */}
        <p className="text-foreground text-base font-bold">
          {product.price}
        </p>

        {/* Source/store */}
        <p className="text-muted-foreground text-xs">
          {product.source}
        </p>

        {/* Rating */}
        {product.rating !== undefined && (
          <div className="flex items-center gap-1">
            <StarRating rating={product.rating} />
            <span className="text-muted-foreground text-[11px]">
              {product.rating.toFixed(1)}
              {product.reviews !== undefined && ` (${product.reviews})`}
            </span>
          </div>
        )}
      </div>
    </a>
  )
}

// --------------

export const PriceComparisonAnnotation: React.FC<{
  annotation: Annotation | Annotation2
}> = ({ annotation }) => {
  const result = (annotation as Record<string, unknown>)['priceComparison__compareProductPrices'] as
    Record<string, unknown> | undefined
  if (!result) return null

  const data: PriceComparisonResult =
    ('result' in result ? result.result : result) as PriceComparisonResult

  if (!data?.products?.length) return null

  return (
    <div className="mt-3 space-y-2">
      {/* Header */}
      <div>
        <p className="text-foreground/80 text-sm font-medium">
          🛒 Price comparison
        </p>
        {data.query && (
          <p className="text-muted-foreground text-xs">
            {data.query}
          </p>
        )}
      </div>

      {/* Scrollable card row */}
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        role="list"
        aria-label="Product price comparison results"
      >
        {data.products.map((product, idx) => (
          <div key={`${product.name}-${idx}`} role="listitem">
            <ProductCard product={product} />
          </div>
        ))}
      </div>
    </div>
  )
}
